import { cva, type VariantProps } from 'class-variance-authority';

import { DARK_CONTROL } from '../../styles/elevation';

/**
 * Textarea visual variants — deliberately mirror the shared Input tokens
 * (`Input.variants.ts`) so the two read as one family: the same translucent
 * `secondary-50/70` surface, `secondary-300` rest border (→ `secondary-400`
 * hover on web), the `secondary-800` "active typing" border, `red/green-600`
 * status, per-size radius (16/18/18/20/20) and text ramp (B2 → B1 from xl).
 *
 * The layout is the one thing that diverges from Input: instead of a fixed
 * height with vertically-centered content, the field GROWS. Its min/max height
 * is set by the component from `minRows`/`maxRows` (× the size's line-height),
 * content sits under a per-size vertical padding (tuned so a 1-row field
 * matches Input's 36/40/44/48/52px height ramp), and row children are centered
 * (`items-center`) so a single line reads centered even when a taller trailing
 * button sets the field height. The trailing slot itself pins to the bottom
 * (`self-end`, set in the component) so the send button stays with the last
 * line as the field grows. The focus ring (keyboard-only) and its per-size
 * radius are reused verbatim from Input (`inputFocusRingVariants`).
 */

/** Root column: label row + field host + helper row, 4px apart. */
export const textareaRootVariants = cva('flex w-full flex-col items-start gap-1', {
  variants: {
    disabled: { true: 'opacity-50', false: '' },
  },
  defaultVariants: { disabled: false },
});

/**
 * Field host — `relative` so the absolute focus-ring overlay positions against
 * it. Height is auto (no fixed height like Input's ring wrapper) so the host
 * grows with the field as content is added.
 */
export const textareaRingVariants = cva('relative w-full');

/** The bordered field surface the text + trailing slot live in. */
export const textareaFieldVariants = cva(
  // Dark: nested, like Input (elevation.ts).
  'flex w-full flex-row items-center overflow-hidden border bg-secondary-50/70 pl-3 ' +
    `${DARK_CONTROL} ` +
    'web:transition-colors web:duration-150',
  {
    variants: {
      // Radius + per-size vertical padding. The padding is tuned so a
      // single-row field equals Input's height ramp for that size
      // (36/40/44/48/52px = lineHeight + 2·py + 2·border), so `size` scales the
      // resting height, not just the text. Native adds an explicit min-height
      // floor at the same ramp: the RN text box hugs the font's natural line
      // (no lineHeight there — see textareaTextVariants), so without the floor
      // a 1-row field would come out a few px short of the ramp.
      size: {
        s: 'rounded-[16px] py-1.5 native:min-h-9',
        m: 'rounded-[18px] py-2 native:min-h-10',
        l: 'rounded-[18px] py-2.5 native:min-h-11',
        xl: 'rounded-[20px] py-[11px] native:min-h-12',
        xxl: 'rounded-[20px] py-[13px] native:min-h-[52px]',
      },
      state: {
        // Dark rest border: `secondary-300`, a quiet hairline the control fill
        // backs up (owner, 2026-10: s400 read too light); hover steps to s400.
        rest: 'border-secondary-300 dark:border-secondary-300 web:hover:border-secondary-400 dark:web:hover:border-secondary-400',
        focused: 'border-secondary-300 dark:border-secondary-400',
        typing: 'border-secondary-800',
      },
      status: {
        none: '',
        error: '!border-red-600',
        success: '!border-green-600',
      },
      // Right padding per slot occupancy (12px text-only, 7px next to a slot).
      rightInset: { text: 'pr-3', slot: 'pr-[7px]' },
    },
    defaultVariants: { size: 'm', state: 'rest', status: 'none', rightInset: 'text' },
  },
);

/**
 * The textarea element. `field-sizing:content` (web) grows it with content
 * between the component's min/max height; unsupported browsers fall back to the
 * min-height floor. Mona Sans Medium, secondary-800 value, B2 → B1 by size.
 *
 * Native gets fontSize-ONLY classes, same as Input (see inputTextVariants):
 * an RN TextInput on iOS draws the typed value low in its line box when an
 * explicit lineHeight (text-b2/b1 bundle one) exceeds the font's natural line,
 * while the placeholder ignores it — value and placeholder visibly disagree
 * and neither centers. No lineHeight = both share font metrics; the field's
 * native min-height ramp (textareaFieldVariants) + items-center own the
 * vertical rhythm for a 1-row field. Web keeps the full text-b2/b1.
 */
export const textareaTextVariants = cva(
  'min-w-0 flex-1 font-medium text-secondary-800 native:font-mona-medium ' +
    'web:caret-secondary-800 web:[field-sizing:content]',
  {
    variants: {
      size: {
        s: 'web:text-b2 native:text-[0.875rem]',
        m: 'web:text-b2 native:text-[0.875rem]',
        l: 'web:text-b2 native:text-[0.875rem]',
        xl: 'web:text-b1 native:text-[1rem]',
        xxl: 'web:text-b1 native:text-[1rem]',
      },
      /** Placeholder: secondary-400 at rest, secondary-500 while focused (web). */
      focused: {
        true: 'web:placeholder:text-fg-subtle',
        false: 'web:placeholder:text-secondary-400',
      },
    },
    defaultVariants: { size: 'm', focused: false },
  },
);

export type TextareaFieldVariantProps = VariantProps<typeof textareaFieldVariants>;

/** Line-height (px) per size at 1× — B2 (22) through l, B1 (24) from xl. */
const LINE_HEIGHT_FOR = { s: 22, m: 22, l: 22, xl: 24, xxl: 24 } as const;

/**
 * The field's vertical padding per size (px), as `textareaFieldVariants` sets
 * it: rem-based through `l` (so it scales with `scale` on native), fixed px
 * from `xl`.
 */
const FIELD_PADDING_Y_FOR = {
  s: { px: 6, rem: true },
  m: { px: 8, rem: true },
  l: { px: 10, rem: true },
  xl: { px: 11, rem: false },
  xxl: { px: 13, rem: false },
} as const;

/**
 * How far the text box bleeds over the field's vertical padding.
 *
 * The padding belongs INSIDE the scrolling text, not around it. Around it,
 * the box's scroll clip sits a padding's depth inside the border, so a
 * scrolled field chops its top line in half against a hard edge in mid-air.
 * Inside it, text scrolls under the padding up to the border like any text
 * view, and at rest sits exactly where it did. `scale` is
 * `useResponsiveScale()` (1 on web).
 */
export function textareaTextInset(
  size: keyof typeof FIELD_PADDING_Y_FOR,
  scale: number,
): number {
  const { px, rem } = FIELD_PADDING_Y_FOR[size];
  return rem ? px * scale : px;
}

/**
 * The text box's min/max height (px) from `minRows`/`maxRows`, including the
 * padding it carries (`insetY`: top + bottom).
 *
 * `scale` is `useResponsiveScale()`: 1 on web (the browser already scales
 * rem), and on native the factor the rem-sized FONT gets — up to 1.25× on a
 * tablet, plus the OS text size. The rows follow the font, so a field shows
 * the same amount of text on an iPad as on a phone.
 */
export function textareaHeightBounds(
  size: keyof typeof LINE_HEIGHT_FOR,
  minRows: number,
  maxRows: number,
  scale: number,
  insetY = 0,
): { minHeight: number; maxHeight: number } {
  const lineHeight = LINE_HEIGHT_FOR[size] * scale;
  return {
    minHeight: minRows * lineHeight + insetY,
    maxHeight: maxRows * lineHeight + insetY,
  };
}
